import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  School, Puzzle, CalendarDays, UserPlus, Sparkles,
  CheckCircle, ChevronRight, Loader2,
} from 'lucide-react';
import { api, apiError } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { useModuleStore } from '../../stores/moduleStore';
import { toast } from '../../stores/notificationStore';

// ─── Types ────────────────────────────────────────────────────────────────────
interface OnboardingState {
  current_step: number;
  completed: boolean;
  step_school_profile: boolean;
  step_modules: boolean;
  step_academic_year: boolean;
  step_admin_user: boolean;
  step_sample_data: boolean;
  sample_data_seeded: boolean;
}

interface ModuleOption {
  slug: string;
  name: string;
  description: string;
  is_core?: boolean;
}

interface SchoolProfileForm {
  name: string; school_type: string; phone: string;
  email: string; address_line1: string; district: string; region: string;
}

interface TermForm { name: string; start_date: string; end_date: string; }

interface AcademicYearForm {
  name: string; start_date: string; end_date: string; terms: TermForm[];
}

interface AdminUserForm {
  first_name: string; last_name: string; phone: string; email: string; role: string;
}

// ─── Step definitions ─────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, key: 'schoolProfile', icon: School,       field: 'step_school_profile' },
  { id: 2, key: 'modules',       icon: Puzzle,       field: 'step_modules'        },
  { id: 3, key: 'academicYear',  icon: CalendarDays, field: 'step_academic_year'  },
  { id: 4, key: 'adminUser',     icon: UserPlus,     field: 'step_admin_user'     },
  { id: 5, key: 'sampleData',    icon: Sparkles,     field: 'step_sample_data'    },
] as const;

const MODULE_OPTIONS: ModuleOption[] = [
  { slug: 'academic',             name: 'Academic',       description: 'Students, attendance, grades, timetable', is_core: true },
  { slug: 'finance',              name: 'Finance',        description: 'Fees, invoices, payments, MoMo'                        },
  { slug: 'hr',                   name: 'HR & Payroll',   description: 'Staff, leave, payroll, payslips'                       },
  { slug: 'library',              name: 'Library',        description: 'Book catalogue and borrowing'                          },
  { slug: 'hostel',               name: 'Hostel',         description: 'Room management and student allocation'                },
  { slug: 'transport',            name: 'Transport',      description: 'Routes and student allocation'                         },
  { slug: 'assets',               name: 'Assets',         description: 'Asset register and tracking'                           },
  { slug: 'analytics_basic',      name: 'Analytics & AI', description: 'Dashboards and AI predictions'                         },
  { slug: 'government_reporting', name: 'Government',     description: 'EMIS, UNEB, UBTEB reporting'                           },
];

const SCHOOL_TYPES = [
  { value: 'primary',    label: 'Primary School'        },
  { value: 'secondary',  label: 'Secondary School'      },
  { value: 'combined',   label: 'Combined School (P–S)' },
  { value: 'university', label: 'University'            },
  { value: 'tertiary',   label: 'Tertiary Institution'  },
  { value: 'vocational', label: 'Vocational / TVET'     },
];

const REGIONS = ['Central', 'Eastern', 'Northern', 'Western'];
const currentYear = new Date().getFullYear();

const DEFAULT_SCHOOL_PROFILE: SchoolProfileForm = {
  name: '', school_type: 'primary', phone: '', email: '',
  address_line1: '', district: '', region: '',
};

const DEFAULT_ACADEMIC_YEAR: AcademicYearForm = {
  name: `${currentYear}`,
  start_date: `${currentYear}-01-01`,
  end_date: `${currentYear}-12-31`,
  terms: [
    { name: 'Term 1', start_date: `${currentYear}-02-01`, end_date: `${currentYear}-04-30` },
    { name: 'Term 2', start_date: `${currentYear}-05-15`, end_date: `${currentYear}-08-15` },
    { name: 'Term 3', start_date: `${currentYear}-09-01`, end_date: `${currentYear}-11-30` },
  ],
};

const DEFAULT_ADMIN_USER: AdminUserForm = {
  first_name: '', last_name: '', phone: '', email: '', role: 'principal',
};

// ─── Shared primitives ────────────────────────────────────────────────────────
function Field({ label, required, children, hint, error }: {
  label: string; required?: boolean; children: React.ReactNode;
  hint?: string; error?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}{required && <span style={{ color: 'var(--red)', marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {error
        ? <p style={{ fontSize: 11, color: 'var(--red)', margin: 0 }}>{error}</p>
        : hint && <p style={{ fontSize: 11, color: 'var(--text-3)', margin: 0 }}>{hint}</p>}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 'var(--radius)',
  border: '1px solid var(--border2)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
};

function Inp(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className="input" style={{ width: '100%' }} {...props} />;
}

function Sel(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      style={{ ...inputStyle, cursor: 'pointer' }}
      {...props}
    />
  );
}

// ─── Step Footer ──────────────────────────────────────────────────────────────
function StepFooter({ onNext, onBack, loading, nextLabel, isLast }: {
  onNext: () => void; onBack?: () => void;
  loading?: boolean; nextLabel?: string; isLast?: boolean;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      paddingTop: 20, borderTop: '1px solid var(--border)', marginTop: 4,
    }}>
      <div>
        {onBack && (
          <button type="button" onClick={onBack} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, color: 'var(--text-3)', fontFamily: 'inherit', padding: '8px 0',
          }}>
            ← Back
          </button>
        )}
      </div>
      <button
        type="button" onClick={onNext} disabled={loading}
        className="btn btn-primary btn-sm"
        style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: loading ? 0.7 : 1 }}
      >
        {loading && <Loader2 size={14} style={{ animation: 'spin 0.6s linear infinite' }} />}
        {nextLabel ?? (isLast ? 'Finish & Go to Dashboard' : 'Continue')}
        {!loading && !isLast && <ChevronRight size={14} />}
      </button>
    </div>
  );
}

// ─── Step 1: School Profile ───────────────────────────────────────────────────
function StepSchoolProfile({ form, setForm, onDone }: {
  form: SchoolProfileForm; setForm: (f: SchoolProfileForm) => void; onDone: () => void;
}) {
  const { t } = useTranslation('onboarding');
  const [errors, setErrors] = useState<Partial<Record<keyof SchoolProfileForm, string>>>({});

  const set = (k: keyof SchoolProfileForm, v: string) => {
    setForm({ ...form, [k]: v });
    if (errors[k]) setErrors(e => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const errs: typeof errors = {};
    if (!form.name.trim()) errs.name = 'School name is required';
    if (!form.school_type) errs.school_type = 'School type is required';
    if (form.phone && !/^\+256[0-9]{9}$/.test(form.phone)) errs.phone = 'Use format +256XXXXXXXXX';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const mut = useMutation({
    mutationFn: () => api.post('/onboarding/step/school-profile', form),
    onSuccess: () => { toast('success', 'School profile saved'); onDone(); },
    onError: (e) => toast('error', apiError(e)),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px 20px' }}>
        <Field label={t('schoolProfile.name')} required error={errors.name}>
          <Inp value={form.name} onChange={e => set('name', e.target.value)} placeholder="St. Mary's Primary School" />
        </Field>
        <Field label={t('schoolProfile.type')} required error={errors.school_type}>
          <Sel value={form.school_type} onChange={e => set('school_type', e.target.value)}>
            {SCHOOL_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Sel>
        </Field>
        <Field label={t('schoolProfile.phone')} error={errors.phone} hint={!errors.phone ? '+256 format (optional)' : undefined}>
          <Inp value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+256700000000" />
        </Field>
        <Field label={t('schoolProfile.email')} error={errors.email}>
          <Inp type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="school@example.com" />
        </Field>
        <Field label={t('schoolProfile.address')}>
          <Inp value={form.address_line1} onChange={e => set('address_line1', e.target.value)} placeholder="Plot 1, Main Street" />
        </Field>
        <Field label={t('schoolProfile.district')}>
          <Inp value={form.district} onChange={e => set('district', e.target.value)} placeholder="Kampala" />
        </Field>
        <Field label={t('schoolProfile.region')}>
          <Sel value={form.region} onChange={e => set('region', e.target.value)}>
            <option value="">Select region…</option>
            {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </Sel>
        </Field>
      </div>
      <StepFooter onNext={() => { if (validate()) mut.mutate(); }} loading={mut.isPending} />
    </div>
  );
}

// ─── Step 2: Modules ──────────────────────────────────────────────────────────
function StepModules({ selected, setSelected, onDone, onBack }: {
  selected: Set<string>; setSelected: (s: Set<string>) => void;
  onDone: () => void; onBack: () => void;
}) {
  const { t } = useTranslation('onboarding');
  const refreshModules = useModuleStore(s => s.refresh);

  const toggle = (slug: string) => {
    if (slug === 'academic') return;
    const n = new Set(selected);
    if (n.has(slug)) { n.delete(slug); } else { n.add(slug); }
    setSelected(n);
  };

  const mut = useMutation({
    mutationFn: () => api.post('/onboarding/step/modules', { modules: Array.from(selected) }),
    onSuccess: async () => { await refreshModules(); toast('success', 'Modules saved'); onDone(); },
    onError: (e) => toast('error', apiError(e)),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <p style={{ fontSize: 13, color: 'var(--text-3)', margin: 0 }}>{t('modules.subtitle')}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
        {MODULE_OPTIONS.map(mod => {
          const on = selected.has(mod.slug);
          return (
            <button
              key={mod.slug} type="button" onClick={() => toggle(mod.slug)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '10px 12px',
                borderRadius: 'var(--radius)',
                border: `2px solid ${on ? 'var(--accent)' : 'var(--border2)'}`,
                background: on ? 'var(--accent-bg)' : 'var(--surface)',
                cursor: mod.is_core ? 'default' : 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s',
                fontFamily: 'inherit',
              }}
            >
              <div style={{
                width: 16, height: 16, borderRadius: 4, flexShrink: 0, marginTop: 1,
                border: `2px solid ${on ? 'var(--accent)' : 'var(--border2)'}`,
                background: on ? 'var(--accent)' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {on && <svg viewBox="0 0 10 8" width="9" height="9"><path d="M1 4l3 3L9 1" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                  {mod.name}
                  {mod.is_core && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--accent)', fontWeight: 400 }}>(required)</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{mod.description}</div>
              </div>
            </button>
          );
        })}
      </div>
      <StepFooter onNext={() => mut.mutate()} onBack={onBack} loading={mut.isPending} />
    </div>
  );
}

// ─── Step 3: Academic Year ────────────────────────────────────────────────────
function StepAcademicYear({ form, setForm, onDone, onBack }: {
  form: AcademicYearForm; setForm: (f: AcademicYearForm) => void;
  onDone: () => void; onBack: () => void;
}) {
  const { t } = useTranslation('onboarding');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setField = (k: keyof Omit<AcademicYearForm, 'terms'>, v: string) => {
    setForm({ ...form, [k]: v });
    if (errors[k]) setErrors(e => { const n = { ...e }; delete n[k]; return n; });
  };

  const setTerm = (i: number, k: keyof TermForm, v: string) => {
    const terms = form.terms.map((t, idx) => idx === i ? { ...t, [k]: v } : t);
    setForm({ ...form, terms });
    const key = `term_${i}_${k}`;
    if (errors[key]) setErrors(e => { const n = { ...e }; delete n[key]; return n; });
  };

  const addTerm = () =>
    setForm({ ...form, terms: [...form.terms, { name: `Term ${form.terms.length + 1}`, start_date: '', end_date: '' }] });

  const removeTerm = (i: number) =>
    setForm({ ...form, terms: form.terms.filter((_, idx) => idx !== i) });

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Year name is required';
    if (!form.start_date) errs.start_date = 'Start date is required';
    if (!form.end_date) errs.end_date = 'End date is required';
    if (form.start_date && form.end_date && form.start_date >= form.end_date) errs.end_date = 'End must be after start';
    form.terms.forEach((term, i) => {
      if (!term.name.trim()) errs[`term_${i}_name`] = 'Required';
      if (!term.start_date) errs[`term_${i}_start_date`] = 'Required';
      if (!term.end_date) errs[`term_${i}_end_date`] = 'Required';
      if (term.start_date && term.end_date && term.start_date >= term.end_date) errs[`term_${i}_end_date`] = 'End must be after start';
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const mut = useMutation({
    mutationFn: () => api.post('/onboarding/step/academic-year', form),
    onSuccess: () => { toast('success', 'Academic year saved'); onDone(); },
    onError: (e) => toast('error', apiError(e)),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px 20px' }}>
        <Field label={t('academicYear.name')} required error={errors.name}>
          <Inp value={form.name} onChange={e => setField('name', e.target.value)} placeholder={`${currentYear}`} />
        </Field>
        <Field label={t('academicYear.startDate')} required error={errors.start_date}>
          <Inp type="date" value={form.start_date} onChange={e => setField('start_date', e.target.value)} />
        </Field>
        <Field label={t('academicYear.endDate')} required error={errors.end_date}>
          <Inp type="date" value={form.end_date} onChange={e => setField('end_date', e.target.value)} />
        </Field>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>{t('academicYear.terms')}</span>
          {form.terms.length < 4 && (
            <button type="button" onClick={addTerm} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 12, color: 'var(--accent)', fontFamily: 'inherit', fontWeight: 500,
            }}>+ Add term</button>
          )}
        </div>
        {form.terms.map((term, i) => (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto',
            gap: '10px 16px', padding: '12px 14px',
            background: 'var(--surface2)', borderRadius: 'var(--radius)',
            border: '1px solid var(--border)',
            alignItems: 'end',
          }}>
            <Field label="Name" required error={errors[`term_${i}_name`]}>
              <Inp value={term.name} onChange={e => setTerm(i, 'name', e.target.value)} placeholder="Term 1" />
            </Field>
            <Field label="Start date" required error={errors[`term_${i}_start_date`]}>
              <Inp type="date" value={term.start_date} onChange={e => setTerm(i, 'start_date', e.target.value)} />
            </Field>
            <Field label="End date" required error={errors[`term_${i}_end_date`]}>
              <Inp type="date" value={term.end_date} onChange={e => setTerm(i, 'end_date', e.target.value)} />
            </Field>
            <div style={{ paddingBottom: 2 }}>
              {i > 0 && (
                <button type="button" onClick={() => removeTerm(i)} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 12, color: 'var(--red)', fontFamily: 'inherit',
                }}>Remove</button>
              )}
            </div>
          </div>
        ))}
      </div>

      <StepFooter onNext={() => { if (validate()) mut.mutate(); }} onBack={onBack} loading={mut.isPending} />
    </div>
  );
}

// ─── Step 4: Admin User ───────────────────────────────────────────────────────
function StepAdminUser({ form, setForm, onDone, onBack }: {
  form: AdminUserForm; setForm: (f: AdminUserForm) => void;
  onDone: () => void; onBack: () => void;
}) {
  const { t } = useTranslation('onboarding');
  const [errors, setErrors] = useState<Partial<Record<keyof AdminUserForm, string>>>({});

  const set = (k: keyof AdminUserForm, v: string) => {
    setForm({ ...form, [k]: v });
    if (errors[k]) setErrors(e => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const errs: typeof errors = {};
    if (!form.first_name.trim()) errs.first_name = 'Required';
    if (!form.last_name.trim()) errs.last_name = 'Required';
    if (!form.phone.trim()) { errs.phone = 'Required'; }
    else if (!/^\+256[0-9]{9}$/.test(form.phone)) { errs.phone = 'Use format +256XXXXXXXXX'; }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const mut = useMutation({
    mutationFn: () => api.post('/onboarding/step/admin-user', form),
    onSuccess: () => { toast('success', 'Admin user saved'); onDone(); },
    onError: (e) => toast('error', apiError(e)),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <p style={{ fontSize: 13, color: 'var(--text-3)', margin: 0 }}>{t('adminUser.subtitle')}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px 20px' }}>
        <Field label={t('adminUser.firstName')} required error={errors.first_name}>
          <Inp value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="John" />
        </Field>
        <Field label={t('adminUser.lastName')} required error={errors.last_name}>
          <Inp value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Doe" />
        </Field>
        <Field label={t('adminUser.phone')} required error={errors.phone} hint={!errors.phone ? '+256 format, e.g. +256700000000' : undefined}>
          <Inp value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+256700000000" />
        </Field>
        <Field label={t('adminUser.email')} error={errors.email}>
          <Inp type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="admin@school.ug" />
        </Field>
        <Field label={t('adminUser.role')} required>
          <Sel value={form.role} onChange={e => set('role', e.target.value)}>
            <option value="principal">Principal</option>
            <option value="school_admin">School Administrator</option>
            <option value="bursar">Bursar</option>
            <option value="school_owner">School Owner</option>
          </Sel>
        </Field>
      </div>
      <StepFooter onNext={() => { if (validate()) mut.mutate(); }} onBack={onBack} loading={mut.isPending} />
    </div>
  );
}

// ─── Step 5: Sample Data ──────────────────────────────────────────────────────
function StepSampleData({ seed, setSeed, onDone: _onDone, onBack }: {
  seed: boolean; setSeed: (v: boolean) => void; onDone: () => void; onBack: () => void;
}) {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();

  const mut = useMutation({
    mutationFn: () => api.post('/onboarding/step/sample-data', { seed_sample_data: seed }),
    onSuccess: () => { toast('success', t('complete.toast')); navigate('/dashboard', { replace: true }); },
    onError: (e) => toast('error', apiError(e)),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <p style={{ fontSize: 13, color: 'var(--text-3)', margin: 0 }}>{t('sampleData.subtitle')}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {([
          { value: false, label: t('sampleData.blank.label'), desc: t('sampleData.blank.desc') },
          { value: true,  label: t('sampleData.sample.label'), desc: t('sampleData.sample.desc') },
        ] as const).map(opt => (
          <button
            key={String(opt.value)} type="button" onClick={() => setSeed(opt.value)}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: '14px 16px', textAlign: 'left',
              borderRadius: 'var(--radius)',
              border: `2px solid ${seed === opt.value ? 'var(--accent)' : 'var(--border2)'}`,
              background: seed === opt.value ? 'var(--accent-bg)' : 'var(--surface)',
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
            }}
          >
            <div style={{
              width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 1,
              border: `2px solid ${seed === opt.value ? 'var(--accent)' : 'var(--border2)'}`,
              background: seed === opt.value ? 'var(--accent)' : 'transparent',
            }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{opt.label}</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>{opt.desc}</div>
            </div>
          </button>
        ))}
      </div>
      <StepFooter onNext={() => mut.mutate()} onBack={onBack} loading={mut.isPending} isLast nextLabel={t('complete.button')} />
    </div>
  );
}

// ─── Step Indicator ───────────────────────────────────────────────────────────
function StepIndicator({ currentStep, stateFlags }: {
  currentStep: number; stateFlags: Record<string, boolean>;
}) {
  const { t } = useTranslation('onboarding');
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, flexWrap: 'wrap', marginBottom: 28 }}>
      {STEPS.map((step, idx) => {
        const done = stateFlags[step.field];
        const active = currentStep === step.id;
        const Icon = step.icon;
        return (
          <div key={step.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 12px', borderRadius: 20,
              fontSize: 12, fontWeight: 500,
              background: done
                ? 'var(--green-bg)'
                : active
                  ? 'var(--accent)'
                  : 'var(--surface)',
              color: done ? 'var(--green)' : active ? '#fff' : 'var(--text-3)',
              border: `1px solid ${done ? 'transparent' : active ? 'transparent' : 'var(--border2)'}`,
              transition: 'all 0.2s',
            }}>
              {done
                ? <CheckCircle size={13} />
                : <Icon size={13} />}
              <span>{t(`steps.${step.key}`)}</span>
            </div>
            {idx < STEPS.length - 1 && (
              <div style={{
                width: 20, height: 1,
                background: done ? 'var(--green)' : 'var(--border2)',
                opacity: 0.5,
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────
export function OnboardingWizard() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const tenant = useAuthStore(s => s.tenant);

  const [schoolProfile, setSchoolProfile] = useState<SchoolProfileForm>({
    ...DEFAULT_SCHOOL_PROFILE, name: tenant?.name ?? '',
  });
  const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set(['academic', 'finance', 'hr']));
  const [academicYear, setAcademicYear] = useState<AcademicYearForm>(DEFAULT_ACADEMIC_YEAR);
  const [adminUser, setAdminUser] = useState<AdminUserForm>(DEFAULT_ADMIN_USER);
  const [seedSampleData, setSeedSampleData] = useState(false);
  const [displayStep, setDisplayStep] = useState<number | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['onboarding-state'],
    queryFn: () => api.get<{ data: OnboardingState }>('/onboarding').then(r => r.data.data),
  });

  const skipMut = useMutation({
    mutationFn: () => api.post('/onboarding/skip'),
    onSuccess: () => navigate('/dashboard', { replace: true }),
    onError: (e) => toast('error', apiError(e)),
  });

  if (data?.completed) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  const serverStep = data?.current_step ?? 1;
  const step = displayStep ?? serverStep;

  const stateFlags: Record<string, boolean> = {
    step_school_profile: data?.step_school_profile ?? false,
    step_modules:        data?.step_modules ?? false,
    step_academic_year:  data?.step_academic_year ?? false,
    step_admin_user:     data?.step_admin_user ?? false,
    step_sample_data:    data?.step_sample_data ?? false,
  };

  const advance = async () => { await refetch(); setDisplayStep(null); };
  const goBack = () => setDisplayStep(s => Math.max(1, (s ?? serverStep) - 1));

  const stepTitles: Record<string, string> = {
    schoolProfile: 'School Profile',
    modules:       'Enable Modules',
    academicYear:  'Academic Year',
    adminUser:     'Admin User',
    sampleData:    'Sample Data',
  };

  const currentStepKey = STEPS[step - 1]?.key ?? 'schoolProfile';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 680 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 52, height: 52, borderRadius: 14,
            background: 'var(--p900)', marginBottom: 16,
          }}>
            <School size={24} color="#fff" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0, fontFamily: "'Space Grotesk', sans-serif" }}>
            {t('title')}
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 6 }}>{t('subtitle')}</p>
        </div>

        {/* Step indicator */}
        {!isLoading && <StepIndicator currentStep={step} stateFlags={stateFlags} />}

        {/* Card */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: '28px 32px',
          boxShadow: '0 2px 16px rgba(38,33,92,0.07)',
        }}>
          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0' }}>
              <Loader2 size={22} style={{ color: 'var(--accent)', animation: 'spin 0.6s linear infinite' }} />
            </div>
          ) : (
            <>
              {/* Step heading */}
              <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0, fontFamily: "'Space Grotesk', sans-serif" }}>
                  {stepTitles[currentStepKey]}
                </h2>
                <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3 }}>
                  Step {step} of {STEPS.length}
                </p>
              </div>

              {step === 1 && <StepSchoolProfile form={schoolProfile} setForm={setSchoolProfile} onDone={advance} />}
              {step === 2 && <StepModules selected={selectedModules} setSelected={setSelectedModules} onDone={advance} onBack={goBack} />}
              {step === 3 && <StepAcademicYear form={academicYear} setForm={setAcademicYear} onDone={advance} onBack={goBack} />}
              {step === 4 && <StepAdminUser form={adminUser} setForm={setAdminUser} onDone={advance} onBack={goBack} />}
              {step === 5 && <StepSampleData seed={seedSampleData} setSeed={setSeedSampleData} onDone={advance} onBack={goBack} />}
            </>
          )}
        </div>

        {/* Skip link */}
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button
            type="button" onClick={() => skipMut.mutate()} disabled={skipMut.isPending}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 12, color: 'var(--text-3)', fontFamily: 'inherit',
              textDecoration: 'underline',
            }}
          >
            {t('skipLink')}
          </button>
        </div>
      </div>
    </div>
  );
}
